import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  TextArea,
  Typography,
} from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { InitialSettingType, InvoiceTypeEnum } from "@/pages/Shop/Invoice/forms/type";
import { FormInstance } from "@payping/web-components/components/Form/types";
import styles from "@/assets/scss/Shop/Invoice/scss/form.module.scss";
import { ProductIntegrationVM } from "@payping/api";
import { useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import { useLocation } from "react-use";
import { ButtonProps } from "@payping/web-components/components/button";
import { SwitchRow } from "@/core/component";

interface Props {
  showInvoiceType: InvoiceTypeEnum;
  setShowInvoiceType: React.Dispatch<React.SetStateAction<InvoiceTypeEnum>>;
  setInvoiceName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setInvoiceNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  next: () => void;
  loading: boolean;
  form: FormInstance<InitialSettingType>;
  enteredText?: string;
  setEnteredText: React.Dispatch<React.SetStateAction<string | undefined>>;
  productGetIntegrationStatus?: ProductIntegrationVM;
  isSchedule?: boolean;
}
type LabelType = "INVOICE_AMOUNT" | "BUSINESS_NAME";
const FirstStepInvoice: React.FC<Props> = (props: Props) => {
  const {
    showInvoiceType,
    setShowInvoiceType,
    next,
    loading,
    setInvoiceName,
    setInvoiceNumber,
    form,
    enteredText,
    setEnteredText,
  } = props;
  const { t } = useAppTranslation();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [businessNameSelected, setBusinessName] = useState(false);
  const [invoiceAmountSelected, setInvoiceAmountSelected] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const constantLabels: LabelType[] = ["INVOICE_AMOUNT", "BUSINESS_NAME"];

  const EditTextArea = (label: LabelType, state: boolean) => {
    let customSMSValue = props.form?.getFieldValue("customReminderMessage") ?? "";
    customSMSValue = state ? customSMSValue.replace(RegExp(label, "g"), "") : customSMSValue + label;
    props.form?.setFieldsValue({ customReminderMessage: customSMSValue });
  };
  // fire on clicking label button
  const labelOnClick = (label: LabelType) => {
    if (label === "BUSINESS_NAME") {
      EditTextArea("BUSINESS_NAME", businessNameSelected);
      setBusinessName(!businessNameSelected);
    } else {
      EditTextArea("INVOICE_AMOUNT", invoiceAmountSelected);
      setInvoiceAmountSelected(!invoiceAmountSelected);
    }
  };

  // change button variant base on label status
  const labelVariant = (label: LabelType): ButtonProps["type"] => {
    if (label === "BUSINESS_NAME") return businessNameSelected ? "link" : "dashed";
    return invoiceAmountSelected ? "link" : "dashed";
  };
  const handleModalSubmit = () => {
    setEnteredText(form.getFieldValue("termsAndConditions"));
    setShowModal(false);
  };

  useEffect(() => {
    form.setFieldsValue({ invoiceType: InvoiceTypeEnum.online });
  }, [form]);

  const onFinish = () => {
    next();
  };

  return (
    <Form<InitialSettingType>
      canDeactivate
      scrollToFirstError={{ behavior: "smooth" }}
      form={form}
      onFinish={onFinish}
      layout={"vertical"}
      initialValues={{ showNotesAfterSuccessPayment: false, showTermsAfterSuccessPayment: false }}
      onValuesChange={(changedValues) => {
        if ("conditions" in changedValues && changedValues.conditions) {
          setShowModal(true);
        }
        if (changedValues.conditions === false) {
          setEnteredText("");
        }
        if ("invoiceType" in changedValues) {
          setShowInvoiceType(changedValues.invoiceType);
        }
        if ("invoiceName" in changedValues) {
          setInvoiceName(changedValues.invoiceName);
        }
        if ("invoiceNumber" in changedValues) {
          setInvoiceNumber(changedValues.invoiceNumber);
        }
      }}
    >
      <Row align={"middle"} gutter={[8, 8]}>
        <Col xs={12} md={8}>
          <Form.Item label={t("invoice.name")} rules={[{ required: true }]} name={"invoiceName"}>
            <Input className={"tw-w-full"} skeleton={loading} />
          </Form.Item>
        </Col>
        <Col xs={10} md={6}>
          <Form.Item label={t("invoice.detail.invoiceNumber")} name={"invoiceNumber"}>
            <Input className={"tw-w-full"} skeleton={loading} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label={t("shop.createInvoice.firstStep.paymentType")} name={"invoiceType"}>
        <Radio.Group size={"middle"}>
          <Radio.Button value={InvoiceTypeEnum.online}>{t("online")}</Radio.Button>
          <Radio.Button value={InvoiceTypeEnum.cash}>{t("common.gateway.Cash")}</Radio.Button>
        </Radio.Group>
      </Form.Item>
      {showInvoiceType === InvoiceTypeEnum.online ? (
        <>
          <Form.Item valuePropName={"checked"} name={"isTextReminder"}>
            <SwitchRow label={t("shop.createInvoice.firstStep.reminderText")} loading={loading} />
          </Form.Item>
          <Form.Item noStyle dependencies={["isTextReminder"]}>
            {({ getFieldValue }) => {
              const isReminder = getFieldValue("isTextReminder");
              return (
                isReminder && (
                  <Row className="tw-w-full" gutter={[8, 8]}>
                    <Col span={24}>
                      <Form.Item className="tw-w-full" name="memo">
                        <Input.TextArea rows={4} skeleton={loading} />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              );
            }}
          </Form.Item>
          <Form.Item valuePropName={"checked"} name={"isTextUser"}>
            <SwitchRow label={t("shop.createInvoice.firstStep.customerMessage")} loading={loading} />
          </Form.Item>
          <Form.Item noStyle dependencies={["isTextUser"]}>
            {({ getFieldValue }) => {
              const isUser = getFieldValue("isTextUser");
              return (
                isUser && (
                  <Row className="tw-w-full" gutter={[8, 8]}>
                    <Col xs={24} lg={6}>
                      <Form.Item name="showNotesAfterSuccessPayment" noStyle>
                        <Select className={"tw-w-full"} skeleton={loading}>
                          <Select.Option value={true}>نمایش پس از پرداخت</Select.Option>
                          <Select.Option value={false}>نمایش قبل و بعد از پرداخت</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item name="notes" noStyle>
                        <Input.TextArea skeleton={loading} />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              );
            }}
          </Form.Item>

          <Form.Item valuePropName={"checked"} name={"conditions"}>
            <SwitchRow label={t("shop.createInvoice.firstStep.rules")} loading={loading} />
          </Form.Item>

          <Form.Item noStyle dependencies={["conditions"]}>
            {({ getFieldValue }) => {
              const terms = getFieldValue("conditions");
              return (
                terms && (
                  <>
                    <Form.Item>
                      <Button className={"!tw-p-0"} type={"link"} onClick={() => setShowModal(true)}>
                        {t("edit")}
                      </Button>
                    </Form.Item>
                    <Form.Item name="showTermsAfterSuccessPayment">
                      <Select skeleton={loading}>
                        <Select.Option value={true}>نمایش پس از پرداخت</Select.Option>
                        <Select.Option value={false}>نمایش قبل و بعد از پرداخت</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item>
                      <Typography.Text type={"secondary"}>{enteredText}</Typography.Text>
                    </Form.Item>
                  </>
                )
              );
            }}
          </Form.Item>

          <Modal
            title={t("shop.createInvoice.firstStep.editRules")}
            open={showModal}
            onCancel={() => setShowModal(false)}
            footer={
              <Space>
                <Button onClick={() => setShowModal(false)}>{t("cancel")}</Button>
                <Button
                  type={"primary"}
                  onClick={() => {
                    handleModalSubmit();
                  }}
                >
                  {t("ok")}
                </Button>
              </Space>
            }
          >
            <Form.Item className="tw-w-full" name="termsAndConditions">
              <Input.TextArea rows={4} skeleton={loading} />
            </Form.Item>
          </Modal>
        </>
      ) : null}

      {props.isSchedule && props.productGetIntegrationStatus?.kavenegar && (
        <>
          <Form.Item valuePropName={"checked"} name={"isCustomerMessage"}>
            <SwitchRow label={t("invoice.create.base.customSMS")} loading={loading} />
          </Form.Item>
          <Form.Item noStyle dependencies={["isCustomerMessage"]}>
            {({ getFieldValue }) => {
              const isCustomSMS = getFieldValue("isCustomerMessage");
              return (
                isCustomSMS && (
                  <Row gutter={[8, 16]} className={"tw-mb-4 tw-w-full"}>
                    <Col span={24}>
                      <Row gutter={8}>
                        {constantLabels.map((label, index) => (
                          <Col key={index}>
                            <Button
                              onClick={() => labelOnClick(label)}
                              type={labelVariant(label)}
                              htmlType={"button"}
                              loading={loading}
                            >
                              {label}
                            </Button>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        rules={[
                          {
                            validator: (_rule, value): Promise<void> =>
                              value && value !== "" && !(businessNameSelected && invoiceAmountSelected)
                                ? Promise.reject(t("error.customSMS"))
                                : Promise.resolve(),
                          },
                        ]}
                        name={"customReminderMessage"}
                      >
                        <TextArea
                          skeleton={loading}
                          rows={4}
                          className="tw-w-full"
                          placeholder={t("error.alert.placeholder")}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Alert
                        type={"warning"}
                        banner
                        showIcon
                        message={t("invoice.create.base.guide")}
                        description={
                          <ul className="!tw-pr-0">
                            <li className={styles.mb8}>
                              <Col>{t("error.alert.sent1")}</Col>
                            </li>
                            <li className={styles.mb8}>
                              <Col>{t("error.alert.sent2")}</Col>
                            </li>
                            <li className={styles.mb8}>
                              <Col>{t("error.alert.sent3")}</Col>
                            </li>
                            <li className={styles.mb8}>
                              <Col>{t("error.alert.sent4")}</Col>
                            </li>
                          </ul>
                        }
                      />
                    </Col>
                  </Row>
                )
              );
            }}
          </Form.Item>
        </>
      )}

      <Form.Item>
        <Row gutter={[8, 8]}>
          <Col flex={2}>
            <Button block type={"primary"} htmlType={"submit"} loading={loading}>
              {t("shop.createInvoice.nextStep")}
            </Button>
          </Col>
          <Col flex={1}>
            <Button
              block
              onClick={() => {
                navigate({ pathname: DashboardRoutes.myShop.invoice.list.url }, { state: location.state });
              }}
            >
              {t("cancel")}
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};

export default FirstStepInvoice;
